import React, { useMemo } from 'react';
import styled, { css } from 'styled-components';
import { Classable, HasChildren, Shapeable } from '@shapeable/types';
import { breakpoints } from '@shapeable/theme';
import { EntityFilters, ExplorerEntityGridLayout, useView } from '@shapeable/ui';
import { compact, includes, uniqBy } from 'lodash';
import { classNames, entityGridNameSort, linkedMatchesFilter } from '@shapeable/utils';
import { useRegions } from '../../hooks/use-regions';
import { Region } from '@shapeable/kelp-forest-alliance-types';
import { PledgeIcon } from '@shapeable/icons';

const cls = classNames('explorer-market-map-layout');

// -------- Types -------->

export type RegionLayoutGridProps = Classable & HasChildren & {
}

export const RegionLayoutGridDefaultProps: RegionLayoutGridProps = {

};

// -------- Child Component Props -------->

type ContainerProps = {

}

// -------- Styles -------->

const ContainerStyles = breakpoints({
  base: css`

  `,
});

const FiltersStyles = breakpoints({
  base: css`
    
  `,
});


// -------- Components -------->

const My = {
  Container: styled(ExplorerEntityGridLayout)<ContainerProps>`${ContainerStyles}`,
    Filters: styled(EntityFilters)`${FiltersStyles}`,
};

export const RegionLayoutGrid: Shapeable.FC<RegionLayoutGridProps> = (props) => {
  const { className } = props;

  const regions = useRegions();
  const view = 'explorer';
  const { filters, selectedTypes } = useView(view);
  const { parent = [] } = filters;

  const regionsList = useMemo(() => regions.filter(region => !region?.children.length), [regions]);

  const parentRegions = useMemo(() => uniqBy(regions.map(region => region?.parent).filter(Boolean), 'id'), [regions]);
  
  const allRegions = useMemo(() => entityGridNameSort(regionsList), [regionsList]);

  const filteredAndToggledRegions = useMemo(() => {
    return allRegions.filter((region: Region) => {
      const matchesFilters = (
        linkedMatchesFilter(compact([region.parent]), parent)
      );

      const isCommittedToChallenge = region.indicatorResults?.some(ir => ir.indicator.slug === 'is-committed-yes-or-no' && ir.value === 'Yes');
      const matchesToggle = includes(selectedTypes, 'isCommitted') ? isCommittedToChallenge : true;
      return matchesFilters && matchesToggle;
    });
  }, [allRegions, selectedTypes, parent]);

  return (
    <My.Container 
      className={cls.name(className)} 
      view={view}
      items={filteredAndToggledRegions}
      spacing={2}
      showSiteFooter={() => false}
      showSearchBar
      maxColumns={4}
      filters={
        <My.Filters 
          view={view}
          results={allRegions}
          selects={compact([
            parentRegions.length > 0 &&
            {
              name: 'parent',
              items: parentRegions,
              pluralLabel: 'Parent Regions',
            },
          ])}
          typeToggles={[
            { 
              label: 'Committed to KFA Challenge', 
              name: 'isCommitted',
              icon: PledgeIcon,
            },
          ]}
        />
      }
    />
  )
};

RegionLayoutGrid.cls = cls;
RegionLayoutGrid.defaultProps = RegionLayoutGridDefaultProps;